import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { Carousel } from "primereact/carousel";
import { Dialog } from "primereact/dialog";
import { InputMask } from "primereact/inputmask";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { ProgressSpinner } from "primereact/progressspinner";
import { RadioButton } from "primereact/radiobutton";
import { Toast } from "primereact/toast";
import { classNames } from "primereact/utils";
import React, { useContext, useEffect, useState } from "react";
import ReCaptcha from "react-google-recaptcha";
import { Controller, useForm } from "react-hook-form";
import { useLocation, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { GALLERY_VIDEO, getErrorTitle, WEBSITE_INQUIRY_TYPES } from "../../../constants/constants";
import { useResponsive } from "../../../responsive/ResponsiveProvider";
import { BusinessHomeProfile, WebsiteInquiry } from "../../../models/businessHomeProfile";
import { AboutSection, AdmissionChecklistSection, AdmissionsSection, BannerSection, CareServicesSection, ComplianceSection, GallerySection, HeaderSection, ProviderAboutSection, RoomsSection, ServiceSection } from "../../../models/manageWebSite";
import { scrollToElement } from "../../../utils/stringUtils";
import { normalizePlainText, openUrlInCurrentTab, openUrlInNewTab } from "../../../utils/securityUtils";
import GoogleMapEmbed from "../../../components/general/googleMap";
import { NO_IMAGE } from "../../../assets";
import WebsiteTemplateRenderer from "../components/WebsiteTemplateRenderer";
import WebsiteSubpageRenderer from "../components/WebsiteSubpageRenderer";
import { DEFAULT_WEBSITE_TEMPLATE_KEY } from "../components/websiteTemplates";

const PublishWeb = () => {
  const { businessName, state, pageSlug } = useParams();
  const location = useLocation();
  const { backendAPIStore } = useContext(MobXProviderContext);
  const toast = React.useRef<Toast>(null);
  const mobile = useResponsive().isMobile;

  const [businessData, setBusinessData] = useState<BusinessHomeProfile | null>(null);
  const [data, setData] = useState<any[]>([]);
  const [headerData, setHeaderData] = useState<HeaderSection | null>(null);
  const [bannerData, setBannerData] = useState<BannerSection | null>(null);
  const [aboutData, setAboutData] = useState<AboutSection | null>(null);
  const [serviceData, setServiceData] = useState<ServiceSection | null>(null);
  const [galleryData, setGalleryData] = useState<GallerySection | null>(null);
  const [roomsData, setRoomsData] = useState<RoomsSection | null>(null);
  const [complianceData, setComplianceData] = useState<ComplianceSection | null>(null);
  const [careServicesData, setCareServicesData] = useState<CareServicesSection | null>(null);
  const [providerAboutData, setProviderAboutData] = useState<ProviderAboutSection | null>(null);
  const [admissionsData, setAdmissionsData] = useState<AdmissionsSection | null>(null);
  const [admissionChecklistData, setAdmissionChecklistData] = useState<AdmissionChecklistSection | null>(null);
  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [recaptchaValue, setRecaptchaValue] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const requestedPageSlug = (pageSlug || "").toLowerCase();
  const normalizedPageSlug = requestedPageSlug === "faq" ? "admissions" : requestedPageSlug;
  const isFaqPage = requestedPageSlug === "faq" || normalizedPageSlug === "admissions";
  const defaultValues = {
    fullName: "",
    email: "",
    phoneNumber: "",
    message: "",
    inquiryType: "",
  };

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm({ defaultValues });

  const navItems = [
    { id: 11, label: "Services" },
    { id: 12, label: "Gallery" },
    { id: 13, label: "Contact" },
  ];

  const getFormErrorMessage = (name: keyof typeof defaultValues) =>
    errors[name] && <small className="p-error">{errors[name]?.message}</small>;

  const renderWarmWelcomeButton = (label: string, onClick: () => void, options?: { outlined?: boolean; type?: "button" | "submit"; loading?: boolean; disabled?: boolean; className?: string; telemetryAction?: string }) => (
    <button
      type={options?.type || "button"}
      onClick={onClick}
      disabled={options?.disabled}
      className={options?.className}
      data-telemetry-action={options?.telemetryAction}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.55rem",
        borderRadius: "999px",
        padding: "0.82rem 1.2rem",
        fontWeight: 700,
        fontSize: "0.98rem",
        lineHeight: 1,
        cursor: options?.disabled ? "not-allowed" : "pointer",
        border: options?.outlined ? "1px solid #b45309" : "1px solid #185da2",
        background: options?.outlined ? "#ffffff" : "#185da2",
        color: options?.outlined ? "#9a3412" : "#ffffff",
        boxShadow: options?.outlined ? "0 12px 24px rgba(180,83,9,0.12)" : "0 14px 28px rgba(24,93,162,0.22)",
        opacity: options?.disabled ? 0.6 : 1,
      }}
    >
      {options?.loading ? <i className="pi pi-spin pi-spinner" style={{ color: options?.outlined ? "#9a3412" : "#ffffff" }} /> : null}
      <span style={{ color: options?.outlined ? "#9a3412" : "#ffffff" }}>{label}</span>
    </button>
  );

  const warmWelcomeHeaderLinkStyle = {
    border: "1px solid rgba(255,255,255,0.24)",
    background: "rgba(255,255,255,0.12)",
    color: "#ffffff",
    borderRadius: "999px",
    padding: "0.6rem 0.9rem",
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    textDecoration: "none",
    lineHeight: 1.2,
  } as const;

  const closeInquiryDialog = () => {
    setDialogVisible(false);
    setRecaptchaValue(null);
  };

  const renderInquiryDialog = () => (
    <Dialog
      header={`Contact ${businessData?.businessName || "this home"}`}
      visible={dialogVisible}
      style={{ width: mobile ? "94vw" : "760px" }}
      onHide={closeInquiryDialog}
    >
      <div
        style={{
          background: "linear-gradient(135deg, #eff6ff 0%, #fffaf3 100%)",
          border: "1px solid #dbe7f3",
          borderRadius: "24px",
          padding: mobile ? "1rem" : "1.25rem",
          marginBottom: "1rem",
        }}
      >
        <div style={{ fontSize: "0.8rem", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "#185da2" }}>
          Request Information
        </div>
        <div style={{ fontSize: "1.3rem", fontWeight: 800, color: "#0f172a", marginTop: "0.45rem" }}>
          Reach out to {businessData?.businessName || "this home"}
        </div>
        <div style={{ color: "#64748b", marginTop: "0.65rem", lineHeight: 1.7 }}>
          Share a few details and the provider will receive your inquiry. Families often ask about availability, pricing, care needs, tours, and move-in timing.
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="grid">
        <div className="col-12 md:col-6">
          <div style={{ display: "grid", gap: "0.45rem" }}>
            <label htmlFor="website-inquiry-fullName" style={{ fontWeight: 700, color: "#334155" }}>Full Name</label>
            <Controller
              name="fullName"
              control={control}
              rules={{ required: "Full name is required." }}
              render={({ field, fieldState }) => (
                <>
                  <InputText
                    {...field}
                    id="website-inquiry-fullName"
                    placeholder="Your full name"
                    className={classNames({ "p-invalid": fieldState.error })}
                    style={{ width: "100%" }}
                  />
                  {getFormErrorMessage("fullName")}
                </>
              )}
            />
          </div>
        </div>
        <div className="col-12 md:col-6">
          <div style={{ display: "grid", gap: "0.45rem" }}>
            <label htmlFor="website-inquiry-email" style={{ fontWeight: 700, color: "#334155" }}>Email</label>
            <Controller
              name="email"
              control={control}
              rules={{ required: "Email is required." }}
              render={({ field, fieldState }) => (
                <>
                  <InputText
                    {...field}
                    id="website-inquiry-email"
                    placeholder="you@example.com"
                    className={classNames({ "p-invalid": fieldState.error })}
                    style={{ width: "100%" }}
                  />
                  {getFormErrorMessage("email")}
                </>
              )}
            />
          </div>
        </div>
        <div className="col-12 md:col-6">
          <div style={{ display: "grid", gap: "0.45rem" }}>
            <label htmlFor="website-inquiry-phone" style={{ fontWeight: 700, color: "#334155" }}>Phone Number</label>
            <Controller
              name="phoneNumber"
              control={control}
              render={({ field }) => (
                <InputMask
                  {...field}
                  id="website-inquiry-phone"
                  mask="(999) 999-9999"
                  placeholder="(555) 555-5555"
                  style={{ width: "100%" }}
                />
              )}
            />
          </div>
        </div>
        <div className="col-12 md:col-6">
          <div
            style={{
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "18px",
              padding: "0.9rem 1rem",
              height: "100%",
            }}
          >
            <div style={{ fontWeight: 700, color: "#334155", marginBottom: "0.65rem" }}>What would you like to know?</div>
            <Controller
              name="inquiryType"
              control={control}
              rules={{ required: "Inquiry type is required." }}
              render={({ field }) => (
                <div className="flex flex-wrap gap-3">
                  {WEBSITE_INQUIRY_TYPES.map((item) => (
                    <div key={item.value} className="flex align-items-center gap-2">
                      <RadioButton inputId={`website-inquiry-${item.value}`} value={item.value} onChange={(e) => field.onChange(e.value)} checked={field.value === item.value} />
                      <label htmlFor={`website-inquiry-${item.value}`}>{item.name}</label>
                    </div>
                  ))}
                </div>
              )}
            />
            {getFormErrorMessage("inquiryType")}
          </div>
        </div>
        <div className="col-12">
          <div style={{ display: "grid", gap: "0.45rem" }}>
            <label htmlFor="website-inquiry-message" style={{ fontWeight: 700, color: "#334155" }}>Message</label>
            <Controller
              name="message"
              control={control}
              rules={{ required: "Message is required." }}
              render={({ field, fieldState }) => (
                <>
                  <InputTextarea
                    {...field}
                    id="website-inquiry-message"
                    rows={5}
                    placeholder="Tell the provider a little about your care needs, preferred move-in timeline, or any questions you have."
                    className={classNames({ "p-invalid": fieldState.error })}
                    style={{ width: "100%" }}
                  />
                  {getFormErrorMessage("message")}
                </>
              )}
            />
          </div>
        </div>
        <div className="col-12">
          <div
            style={{
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: "18px",
              padding: mobile ? "0.85rem" : "1rem",
              display: "flex",
              justifyContent: mobile ? "center" : "flex-start",
              overflowX: "auto",
            }}
          >
            <ReCaptcha sitekey={process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY} onChange={(value) => setRecaptchaValue(value)} />
          </div>
        </div>
        <div className="col-12 flex justify-content-end">
          <Button type="submit" label="Send Inquiry" severity="warning" outlined rounded loading={formSubmitting} disabled={!recaptchaValue} data-telemetry-action="website_send_inquiry" />
        </div>
      </form>
    </Dialog>
  );

  const getWebsiteSessionId = () => {
    let sessionId = localStorage.getItem("sessionId");
    if (!sessionId) {
      sessionId = uuidv4();
      localStorage.setItem("sessionId", sessionId);
    }
    return sessionId;
  };

  const trackWebsiteMetric = async (actionType: string, page: string = normalizedPageSlug || "home") => {
    if (!businessData?.homeId) {
      return;
    }

    try {
      await backendAPIStore.init();
      await backendAPIStore.addWebsitePageMetrics({
        homeId: businessData.homeId,
        sessionId: getWebsiteSessionId(),
        actionType,
        pageSlug: page || "home",
      });
    } catch (metricError) {
      console.error("Error tracking website metric:", metricError);
    }
  };

  const openInquiryDialog = () => {
    setDialogVisible(true);
    void trackWebsiteMetric("REQUEST_INFO_OPEN");
  };

  useEffect(() => {
    void trackWebsiteMetric("PAGE_VIEW");
  }, [businessData?.homeId, normalizedPageSlug]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await backendAPIStore.getPublishedWebsiteDetails(state!, businessName!);
        setData(response.Items || []);
        setBusinessData(response.businessDetails);
        setError(null);
      } catch (fetchError: any) {
        console.error("Error fetching business home details:", fetchError?.response?.data?.error);
        setError("Page Not Found.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    const ele = document.getElementById("#footer");
    if (ele) {
      ele.style.display = "none";
    }

    return () => {
      if (ele) {
        ele.style.display = "block";
      }
    };
  }, [backendAPIStore, businessName, state]);

  useEffect(() => {
    if (data.length === 0) {
      return;
    }

    setHeaderData(data.find((obj) => obj.websiteId?.split("#").pop() === "HEADER") || null);
    setBannerData(data.find((obj) => obj.websiteId?.split("#").pop() === "BANNER") || null);
    setAboutData(data.find((obj) => obj.websiteId?.split("#").pop() === "ABOUT") || null);
    setServiceData(data.find((obj) => obj.websiteId?.split("#").pop() === "SERVICE") || null);
    setGalleryData(data.find((obj) => obj.websiteId?.split("#").pop() === "GALLERY") || null);
    setRoomsData(data.find((obj) => obj.websiteId?.split("#").pop() === "ROOMS") || null);
    setComplianceData(data.find((obj) => obj.websiteId?.split("#").pop() === "COMPLIANCE") || null);
    setCareServicesData(data.find((obj) => obj.websiteId?.split("#").pop() === "CARE_SERVICES") || null);
    setProviderAboutData(data.find((obj) => obj.websiteId?.split("#").pop() === "PROVIDER_ABOUT") || null);
    setAdmissionsData(data.find((obj) => obj.websiteId?.split("#").pop() === "ADMISSIONS") || null);
    setAdmissionChecklistData(data.find((obj) => obj.websiteId?.split("#").pop() === "ADMISSION_CHECKLIST") || null);
  }, [data]);

  const onSubmit = async (formData: any) => {
    setFormSubmitting(true);

    const requestData: WebsiteInquiry = {
      ...formData,
      fullName: normalizePlainText(formData.fullName, { maxLength: 100 }),
      email: normalizePlainText(formData.email, { maxLength: 160 }).toLowerCase(),
      phoneNumber: normalizePlainText(formData.phoneNumber, { maxLength: 25 }),
      message: normalizePlainText(formData.message, { multiline: true, maxLength: 500 }),
      inquiryType: normalizePlainText(formData.inquiryType, { maxLength: 60 }),
      homeId: businessData?.homeId || "",
    };

    try {
      await backendAPIStore.addWebsiteInquiry(requestData);
      toast.current?.show({ severity: "success", summary: "Success", detail: "Your request has been submitted successfully.", life: 3000 });
      setDialogVisible(false);
      setRecaptchaValue(null);
      reset();
    } catch (submitError) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(submitError), detail: "Failed to submit the form. Please try again.", life: 3000 });
    } finally {
      setFormSubmitting(false);
    }
  };

  const images = galleryData?.sectionSignedImg || [];
  const publicPathPrefix = location.pathname.startsWith("/home/") ? "/home" : "/site";
  const websiteBasePath = `${publicPathPrefix}/${encodeURIComponent(state || "")}/${encodeURIComponent(businessName || "")}`;
  const pageLinks = [
    { key: "home", label: "Home", href: websiteBasePath, active: !normalizedPageSlug },
    ...(careServicesData?.enabled ? [{ key: "care-services", label: "Care Services", href: `${websiteBasePath}/care-services`, active: normalizedPageSlug === "care-services" }] : []),
    ...(providerAboutData?.enabled ? [{ key: "about", label: "About", href: `${websiteBasePath}/about`, active: normalizedPageSlug === "about" }] : []),
    ...(admissionsData?.enabled ? [{ key: "admissions", label: "FAQs", href: `${websiteBasePath}/faq`, active: isFaqPage }] : []),
    ...(admissionChecklistData?.enabled ? [{ key: "admission-checklist", label: "Checklist", href: `${websiteBasePath}/admission-checklist`, active: normalizedPageSlug === "admission-checklist" }] : []),
    ...(roomsData?.enabled ? [{ key: "rooms", label: "Rooms", href: `${websiteBasePath}/rooms`, active: normalizedPageSlug === "rooms" }] : []),
    ...(complianceData?.enabled ? [{ key: "compliance", label: "Compliance", href: `${websiteBasePath}/compliance`, active: normalizedPageSlug === "compliance" }] : []),
  ];
  const isSubPage = ["rooms", "compliance", "care-services", "about", "admissions", "admission-checklist"].includes(normalizedPageSlug);

  const openGalleryLightbox = (index: number) => {
    setActiveImageIndex(index);
    setShowAll(true);
  };

  const showPreviousImage = () => {
    if (images.length === 0) {
      return;
    }
    setActiveImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const showNextImage = () => {
    if (images.length === 0) {
      return;
    }
    setActiveImageIndex((prev) => (prev + 1) % images.length);
  };

  useEffect(() => {
    if (!showAll) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        showPreviousImage();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        showNextImage();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showAll, images.length]);

  if (loading) {
    return (
      <div className="col-12 text-center d-flex justify-content-center align-items-center">
        <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
      </div>
    );
  }

  if (data.length === 0 && error) {
    return (
      <div style={{ height: "50vh" }} className="d-flex justify-content-center align-items-center">
        <p className="error" style={{ fontSize: "18px" }}>
          {error}
        </p>
      </div>
    );
  }

  const publishedTemplateKey = businessData?.websitePublishedTemplateKey || DEFAULT_WEBSITE_TEMPLATE_KEY;

  if (isSubPage) {
    return (
      <div style={{ width: "100%", display: "flex", flexDirection: "column", background: "#f7fbff" }}>
        <Toast ref={toast} />
        <WebsiteSubpageRenderer
          templateKey={publishedTemplateKey}
          businessData={businessData}
          roomsData={roomsData}
          complianceData={complianceData}
          careServicesData={careServicesData}
          providerAboutData={providerAboutData}
          admissionsData={admissionsData}
          admissionChecklistData={admissionChecklistData}
          pageSlug={normalizedPageSlug as "rooms" | "compliance" | "care-services" | "about" | "admissions" | "admission-checklist"}
          pageLinks={pageLinks}
          onOpenInquiry={openInquiryDialog}
        />
        {renderInquiryDialog()}
      </div>
    );
  }

  if (!pageSlug) {
    return (
      <div style={{ width: "100%", display: "flex", flexDirection: "column", background: "#f7fbff" }}>
        <Toast ref={toast} />
        <div style={{ background: "#ffffff", borderBottom: "1px solid #e2e8f0" }}>
          <div style={{ maxWidth: "1180px", margin: "0 auto", padding: "0.95rem 1rem", display: "flex", flexWrap: "wrap", gap: "0.75rem" }}>
            {pageLinks.map((link) => (
              <a
                key={link.key}
                href={link.href}
                style={{
                  borderRadius: "999px",
                  padding: "0.65rem 0.95rem",
                  fontWeight: 700,
                  border: link.active ? "none" : "1px solid #dbe7f3",
                  background: link.active ? "#185da2" : "#f8fbff",
                  color: link.active ? "#ffffff" : "#185da2",
                  textDecoration: "none",
                }}
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>
        <WebsiteTemplateRenderer
          templateKey={publishedTemplateKey}
          businessData={businessData}
          headerData={headerData}
          bannerData={bannerData}
          aboutData={aboutData}
          serviceData={serviceData}
          galleryData={galleryData}
          roomsData={roomsData}
          complianceData={complianceData}
          careServicesData={careServicesData}
          providerAboutData={providerAboutData}
          admissionsData={admissionsData}
          admissionChecklistData={admissionChecklistData}
          onOpenInquiry={openInquiryDialog}
        />
        {renderInquiryDialog()}
      </div>
    );
  }

  if (pageSlug && !isSubPage) {
    return (
      <div style={{ height: "50vh" }} className="d-flex justify-content-center align-items-center">
        <p className="error" style={{ fontSize: "18px" }}>
          Page Not Found.
        </p>
      </div>
    );
  }

  if (normalizedPageSlug === "rooms" && !roomsData?.enabled) {
    return (
      <div style={{ height: "50vh" }} className="d-flex justify-content-center align-items-center">
        <p className="error" style={{ fontSize: "18px" }}>
          Rooms page is not available.
        </p>
      </div>
    );
  }

  if (normalizedPageSlug === "compliance" && !complianceData?.enabled) {
    return (
      <div style={{ height: "50vh" }} className="d-flex justify-content-center align-items-center">
        <p className="error" style={{ fontSize: "18px" }}>
          Compliance page is not available.
        </p>
      </div>
    );
  }

  if (normalizedPageSlug === "care-services" && !careServicesData?.enabled) {
    return (
      <div style={{ height: "50vh" }} className="d-flex justify-content-center align-items-center">
        <p className="error" style={{ fontSize: "18px" }}>
          Care Services page is not available.
        </p>
      </div>
    );
  }

  if (normalizedPageSlug === "about" && !providerAboutData?.enabled) {
    return (
      <div style={{ height: "50vh" }} className="d-flex justify-content-center align-items-center">
        <p className="error" style={{ fontSize: "18px" }}>
          About page is not available.
        </p>
      </div>
    );
  }

  if (isFaqPage && !admissionsData?.enabled) {
    return (
      <div style={{ height: "50vh" }} className="d-flex justify-content-center align-items-center">
        <p className="error" style={{ fontSize: "18px" }}>
          FAQ page is not available.
        </p>
      </div>
    );
  }

  if (normalizedPageSlug === "admission-checklist" && !admissionChecklistData?.enabled) {
    return (
      <div style={{ height: "50vh" }} className="d-flex justify-content-center align-items-center">
        <p className="error" style={{ fontSize: "18px" }}>
          Admission checklist page is not available.
        </p>
      </div>
    );
  }

  return (
    <div style={{ width: "100%", display: "flex", flexDirection: "column", background: "#f7fbff" }}>
      <Toast ref={toast} />

      <div
        style={{
          width: "100%",
          background: "linear-gradient(135deg, #0f3d63 0%, #185da2 52%, #f7a64a 100%)",
          padding: mobile ? "1rem" : "1.25rem",
        }}
      >
        <div
          className="flex flex-wrap align-items-center justify-content-between gap-3"
          style={{
            maxWidth: "1180px",
            margin: "0 auto",
            background: "rgba(255,255,255,0.12)",
            border: "1px solid rgba(255,255,255,0.18)",
            borderRadius: "24px",
            padding: "0.95rem 1rem",
            backdropFilter: "blur(10px)",
          }}
        >
          <div className="flex align-items-center gap-3">
            <div
              style={{
                width: "4.6rem",
                height: "4.6rem",
                borderRadius: "20px",
                background: "rgba(255,255,255,0.96)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                boxShadow: "0 14px 28px rgba(15, 61, 99, 0.22)",
              }}
            >
              <img src={headerData?.logoSignedImg || NO_IMAGE} width="64" height="64" style={{ objectFit: "contain", backgroundColor: "transparent" }} />
            </div>
            <div>
              <div style={{ color: "#ffffff", fontWeight: 800, fontSize: "1.2rem" }}>{businessData?.businessName}</div>
              {headerData?.title ? (
                <div style={{ color: "rgba(255,255,255,0.92)", fontSize: "0.98rem", fontWeight: 600, marginTop: "0.35rem" }}>
                  {headerData.title}
                </div>
              ) : null}
            </div>
          </div>

          <div className="flex flex-wrap align-items-center gap-2">
            {navItems.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => scrollToElement(`${item.id}`)}
                style={{ ...warmWelcomeHeaderLinkStyle, cursor: "pointer" }}
              >
                {item.label}
              </button>
            ))}
            {pageLinks.filter((item) => item.key !== "home").map((item) => (
              <a
                key={item.key}
                href={item.href}
                style={warmWelcomeHeaderLinkStyle}
              >
                {item.label}
              </a>
            ))}
          </div>
        </div>
      </div>

      <div
        style={{
          width: "100%",
          background: "linear-gradient(180deg, #f7fbff 0%, #eef6ff 100%)",
          padding: mobile ? "2rem 1.25rem" : "4rem 1.75rem",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: "1180px",
            margin: "0 auto",
            borderRadius: "32px",
            background: "linear-gradient(135deg, rgba(15,61,99,0.96) 0%, rgba(24,93,162,0.94) 58%, rgba(247,166,74,0.92) 100%)",
            boxShadow: "0 30px 60px rgba(15, 61, 99, 0.18)",
            padding: mobile ? "2rem 1.35rem" : "3rem",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "-80px",
              right: "-40px",
              width: "220px",
              height: "220px",
              borderRadius: "999px",
              background: "rgba(255,255,255,0.08)",
            }}
          />
          <div style={{ position: "relative", maxWidth: mobile ? "100%" : "760px" }}>
            <div style={{ textTransform: "uppercase", color: "rgba(255,255,255,0.84)", fontSize: "0.85rem", letterSpacing: "0.14em", marginBottom: "1rem", fontWeight: 700 }}>
              Warm, professional adult family home care
            </div>
            <div style={{ color: "#ffffff", fontSize: mobile ? "2.1rem" : "4rem", lineHeight: 1.05, fontWeight: 800, marginBottom: "1rem" }}>
              {businessData?.businessName}
            </div>
            <div style={{ color: "rgba(255,255,255,0.9)", fontSize: mobile ? "1rem" : "1.15rem", lineHeight: 1.8 }}>
              Personalized senior care services in {businessData?.city}, {businessData?.state}, with a welcoming home environment, clearer communication, and thoughtful day-to-day support.
            </div>
            <div className="grid mt-4" style={{ maxWidth: "720px" }}>
              <div className="col-12 md:col-4">
                <div style={{ background: "rgba(255,255,255,0.12)", borderRadius: "22px", padding: "0.95rem 1rem", border: "1px solid rgba(255,255,255,0.12)" }}>
                  <div style={{ color: "rgba(255,255,255,0.72)", fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.1em" }}>Location</div>
                  <div style={{ color: "#ffffff", fontWeight: 700, marginTop: "0.45rem" }}>{businessData?.city}, {businessData?.state}</div>
                </div>
              </div>
              <div className="col-12 md:col-4">
                <div style={{ background: "rgba(255,255,255,0.12)", borderRadius: "22px", padding: "0.95rem 1rem", border: "1px solid rgba(255,255,255,0.12)" }}>
                  <div style={{ color: "rgba(255,255,255,0.72)", fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.1em" }}>Setting</div>
                  <div style={{ color: "#ffffff", fontWeight: 700, marginTop: "0.45rem" }}>Adult family home</div>
                </div>
              </div>
              <div className="col-12 md:col-4">
                <div style={{ background: "rgba(255,255,255,0.12)", borderRadius: "22px", padding: "0.95rem 1rem", border: "1px solid rgba(255,255,255,0.12)" }}>
                  <div style={{ color: "rgba(255,255,255,0.72)", fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.1em" }}>Experience</div>
                  <div style={{ color: "#ffffff", fontWeight: 700, marginTop: "0.45rem" }}>Care-led presentation</div>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              <span style={{ background: "rgba(255,255,255,0.14)", color: "#ffffff", borderRadius: "999px", padding: "0.65rem 0.95rem", fontWeight: 600 }}>
                Private home setting
              </span>
              <span style={{ background: "rgba(255,255,255,0.14)", color: "#ffffff", borderRadius: "999px", padding: "0.65rem 0.95rem", fontWeight: 600 }}>
                Family-friendly communication
              </span>
              <span style={{ background: "rgba(255,255,255,0.14)", color: "#ffffff", borderRadius: "999px", padding: "0.65rem 0.95rem", fontWeight: 600 }}>
                Professional care presentation
              </span>
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          width: "100%",
          padding: mobile ? "1.5rem 1rem 2rem" : "2rem 1.5rem 3rem",
          background: `linear-gradient(180deg, rgba(255,247,237,0.55) 0%, rgba(248,251,255,0.82) 100%), url(${bannerData?.backgroundSignedImg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div
          style={{
            maxWidth: "1180px",
            margin: "0 auto",
            background: "rgba(255,255,255,0.88)",
            border: "1px solid rgba(217,232,245,0.9)",
            borderRadius: "32px",
            padding: mobile ? "1.25rem" : "1.5rem",
            boxShadow: "0 26px 50px rgba(15,61,99,0.12)",
            backdropFilter: "blur(10px)",
          }}
        >
          <div className="grid align-items-center">
            <div className="col-12 lg:col-6">
              <div style={{ color: "#9a3412", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", fontSize: "0.8rem" }}>
                Welcome families with confidence
              </div>
              <div style={{ color: "#0f172a", fontSize: mobile ? "1.55rem" : "2.3rem", lineHeight: 1.25, fontWeight: 800, marginTop: "0.9rem", whiteSpace: "pre-line" }}>
                {bannerData?.description}
              </div>
              <div style={{ color: "#5f7488", lineHeight: 1.85, marginTop: "1rem", maxWidth: "560px" }}>
                Families should quickly understand the environment, care approach, and warmth of the home. This section gives the website a clear, welcoming first impression.
              </div>
              <div className="mt-4">
                {renderWarmWelcomeButton("Contact Us", openInquiryDialog, { telemetryAction: "website_open_inquiry_dialog_hero" })}
              </div>
            </div>
            <div className="col-12 lg:col-6 flex justify-content-center">
              <img
                src={bannerData?.sectionSignedImg}
                style={{
                  objectFit: "cover",
                  width: "100%",
                  maxWidth: mobile ? "100%" : "520px",
                  height: mobile ? "260px" : "420px",
                  borderRadius: "26px",
                  boxShadow: "0 22px 44px rgba(15,61,99,0.14)",
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          width: "100%",
          padding: mobile ? "2rem 1rem" : "3rem 1.5rem",
          background: "linear-gradient(180deg, #f8fbff 0%, #eef6ff 100%)",
        }}
      >
        <div
          style={{
            maxWidth: "1180px",
            margin: "0 auto",
            background: "#ffffff",
            border: "1px solid #d9e8f5",
            borderRadius: "32px",
            boxShadow: "0 26px 50px rgba(15,61,99,0.1)",
            padding: mobile ? "1rem" : "1.5rem",
          }}
        >
          <div
            className="grid align-items-center"
            style={{
              display: "grid",
              gridTemplateColumns: mobile ? "1fr" : "minmax(320px, 460px) minmax(0, 1fr)",
              gap: mobile ? "1rem" : "2rem",
            }}
          >
            <div className="flex justify-content-center align-items-center">
              <img
                src={aboutData?.sectionSignedImg}
                style={{
                  objectFit: "cover",
                  height: mobile ? "280px" : "520px",
                  width: "100%",
                  maxWidth: "460px",
                  borderRadius: "26px",
                  boxShadow: "0 18px 38px rgba(15,61,99,0.12)",
                }}
              />
            </div>
            <div className="flex justify-content-center align-items-center">
              <div className="w-100 h-100 d-flex flex-column gap-2" style={{ padding: mobile ? "0.5rem 0.25rem" : "0.5rem 1rem 0.5rem 0.25rem" }}>
                <div style={{ color: "#9a3412", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", fontSize: "0.8rem" }}>
                  About the home
                </div>
                <p style={{ fontSize: mobile ? "1.45rem" : "2.25rem", lineHeight: 1.25, color: "#0f172a", fontWeight: 800, margin: "0.9rem 0 0" }}>
                  {aboutData?.title}
                </p>
                <p style={{ fontSize: mobile ? "0.98rem" : "1.08rem", lineHeight: 1.9, color: "#5f7488", margin: "1rem 0 0", whiteSpace: "pre-line" }}>
                  {aboutData?.description}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div id="11" style={{ width: "100%", background: "linear-gradient(180deg, #fffaf3 0%, #f8fbff 100%)", padding: "3rem 1.25rem" }}>
        <div style={{ maxWidth: "1180px", margin: "0 auto" }}>
          <div className="flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
            <div>
              <div style={{ color: "#9a3412", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", fontSize: "0.8rem" }}>
                Services and activities
              </div>
              <div style={{ color: "#0f172a", fontSize: "2rem", fontWeight: 800, marginTop: "0.65rem" }}>
                Care and daily life highlights
              </div>
            </div>
            {renderWarmWelcomeButton("Get in touch", openInquiryDialog, { telemetryAction: "website_open_inquiry_dialog_contact" })}
          </div>

          <div className="grid">
            <div className="col-12 lg:col-6">
              <div style={{ background: "#ffffff", border: "1px solid #d9e8f5", borderRadius: "28px", padding: "1.5rem", boxShadow: "0 20px 40px rgba(15,61,99,0.08)", height: "100%" }}>
                <div style={{ textTransform: "uppercase", fontWeight: 800, fontSize: "0.85rem", letterSpacing: "0.1em", color: "#185da2", marginBottom: "1rem" }}>
                  Services
                </div>
                <div style={{ color: "#5f7488", lineHeight: 1.75, marginBottom: "1rem" }}>
                  Highlight the care support, hospitality, and daily routines families can expect when they reach out.
                </div>
                <ul style={{ paddingLeft: "1.1rem", margin: 0, color: "#36546c", lineHeight: 1.9 }}>
                  {serviceData?.services.map((item) => (
                    <li key={uuidv4()} className="p-1">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="col-12 lg:col-6">
              <div style={{ background: "#ffffff", border: "1px solid #d9e8f5", borderRadius: "28px", padding: "1.5rem", boxShadow: "0 20px 40px rgba(15,61,99,0.08)", height: "100%" }}>
                <div style={{ textTransform: "uppercase", fontWeight: 800, fontSize: "0.85rem", letterSpacing: "0.1em", color: "#b45309", marginBottom: "1rem" }}>
                  Activities
                </div>
                <div style={{ color: "#5f7488", lineHeight: 1.75, marginBottom: "1rem" }}>
                  Show what life feels like in the home, from engagement and community to comfortable everyday living.
                </div>
                <ul style={{ paddingLeft: "1.1rem", margin: 0, color: "#36546c", lineHeight: 1.9 }}>
                  {serviceData?.activities.map((item) => (
                    <li key={uuidv4()} className="p-1">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div id="12" style={{ width: "100%", padding: "3rem 1.25rem", background: "linear-gradient(180deg, #0f3d63 0%, #143f66 100%)" }}>
        <div style={{ maxWidth: "1180px", margin: "0 auto" }}>
          <div className="flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
            <div>
              <div style={{ color: "rgba(255,255,255,0.76)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", fontSize: "0.8rem" }}>
                Gallery
              </div>
              <div style={{ color: "#ffffff", fontSize: mobile ? "1.8rem" : "2.3rem", fontWeight: 800, marginTop: "0.65rem" }}>
                Inside the home
              </div>
              <div style={{ color: "rgba(255,255,255,0.82)", marginTop: "0.8rem", lineHeight: 1.8, maxWidth: "760px", whiteSpace: "pre-line" }}>
                {galleryData?.description}
              </div>
            </div>
            {renderWarmWelcomeButton("View full gallery", () => setShowAll(true), { telemetryAction: "website_open_full_gallery" })}
          </div>

          {images.length > 0 && (
            <div className="w-100 d-flex flex-column gap-3 justify-content-center align-items-center" style={{ padding: mobile ? "10px 0 0" : "18px 0 0" }}>
              <Carousel
                value={images}
                numVisible={mobile ? 1 : 3}
                numScroll={mobile ? 1 : 3}
                circular
                className="w-100"
                itemTemplate={(image) => {
                  const imageIndex = images.indexOf(image);
                  return (
                    <div onClick={() => openGalleryLightbox(imageIndex >= 0 ? imageIndex : 0)} style={{ height: mobile ? "210px" : "280px", padding: "10px", cursor: "pointer" }}>
                      <div style={{ background: "#ffffff", borderRadius: "24px", overflow: "hidden", height: "100%", boxShadow: "0 20px 40px rgba(2, 8, 23, 0.22)" }}>
                        {image && image.indexOf(GALLERY_VIDEO) !== -1 ? (
                          <video width="100%" height="100%" style={{ objectFit: "cover" }} controls>
                            <source src={image} type="video/mp4" />
                            Your browser does not support the video tag.
                          </video>
                        ) : (
                          <img src={image} alt="Gallery" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        )}
                      </div>
                    </div>
                  )
                }}
              />
            </div>
          )}
        </div>
      </div>

      <div id="13" style={{ width: "100%", background: "linear-gradient(180deg, #fffaf3 0%, #f8fbff 100%)", padding: "3rem 1.25rem" }}>
        <div style={{ maxWidth: "1180px", width: "100%", margin: "0 auto", textAlign: "center" }}>
          <div className="mb-4" style={{ textAlign: "center" }}>
            <div style={{ color: "#9a3412", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", fontSize: "0.8rem" }}>
              Contact and location
            </div>
            <div style={{ color: "#0f172a", fontSize: mobile ? "1.85rem" : "2.35rem", fontWeight: 800, marginTop: "0.75rem" }}>
              Make it easy for families to reach out
            </div>
            <div style={{ color: "#5f7488", lineHeight: 1.8, maxWidth: "760px", margin: "0.85rem auto 0" }}>
              A clear final section builds trust fast. Keep your address, map, phone number, and inquiry flow polished and easy to scan.
            </div>
          </div>
          <div className="m-0 mb-4 flex align-items-center justify-content-center flex-wrap">
            {navItems.slice(0, 2).map((item) => (
              <span
                key={item.id}
                style={{ color: "#185da2", cursor: "pointer", margin: "0.75em", fontSize: "1rem", fontWeight: 700 }}
                onClick={() => scrollToElement(`${item.id}`)}
              >
                {item.label}
              </span>
            ))}
            <span
              style={{ color: "#185da2", cursor: "pointer", margin: "0.75em", fontSize: "1rem", fontWeight: 700 }}
              onClick={openInquiryDialog}
            >
              Contact us
            </span>
          </div>

          <div className="grid justify-content-center">
            <div className="col-12 lg:col-7">
              <div style={{ background: "#ffffff", border: "1px solid #d9e8f5", borderRadius: "28px", padding: "1.35rem", boxShadow: "0 22px 44px rgba(15,61,99,0.08)", height: "100%" }} className="flex align-items-center justify-content-center flex-column">
                <div className="w-100 flex align-items-center justify-content-center">
                  <GoogleMapEmbed lat={businessData?.latitude} lng={businessData?.longitude} />
                </div>
                <div className="mt-3">
                  {renderWarmWelcomeButton("Open contact form", openInquiryDialog, { telemetryAction: "website_open_inquiry_dialog_section" })}
                </div>
              </div>
            </div>
            <div className="col-12 lg:col-5">
              <div style={{ background: "linear-gradient(135deg, #0f3d63 0%, #185da2 100%)", borderRadius: "28px", padding: "1.6rem", boxShadow: "0 26px 50px rgba(15,61,99,0.14)", height: "100%", textAlign: "center", margin: "0 auto", width: "100%" }} className="flex align-items-center justify-content-center flex-column">
                <div style={{ color: "rgba(255,255,255,0.76)", fontSize: "0.82rem", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.12em" }}>Contact</div>
                <div className="mt-3" style={{ color: "#ffffff", fontSize: "2rem", fontWeight: 800 }}>{businessData?.businessName}</div>
                <div className="mt-3" style={{ color: "rgba(255,255,255,0.86)", lineHeight: 1.8 }}>{businessData?.formattedAddress}</div>
                <div className="mt-3" style={{ color: "rgba(255,255,255,0.76)", lineHeight: 1.8 }}>
                  Invite families, hospital discharge teams, and referral partners to contact the home with confidence.
                </div>
                <div className="mt-4 flex align-items-center justify-content-center" style={{ color: "white" }}>
                  <i className="pi pi-phone" style={{ marginRight: "0.5em" }}></i>
                  <span
                    style={{ color: "white", cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => {
                      openUrlInCurrentTab(`tel:${businessData?.businessPhoneNumber}`);
                    }}
                  >
                    <InputMask
                      mask="(999) 999-9999"
                      value={businessData?.businessPhoneNumber}
                      disabled
                      style={{ border: "none", color: "white", backgroundColor: "transparent", textDecoration: "underline", cursor: "pointer" }}
                    />
                  </span>
                </div>
                <div className="mt-4" style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.16)", borderRadius: "20px", padding: "0.9rem 1rem", color: "#ffffff", lineHeight: 1.7, width: "100%" }}>
                  Simple, fast, family-friendly contact experience
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-5" style={{ color: "#5f7488" }}>
            <div>
              Copyright 2026 - Adult Family Home Hub LLC
              <i
                className="pi pi-globe m-2"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  openUrlInNewTab("https://www.afhhub.com");
                }}
              ></i>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        visible={showAll}
        onHide={() => setShowAll(false)}
        maximized
        focusOnShow={false}
        style={{ background: "#020617" }}
        contentStyle={{ background: "#020617", padding: 0 }}
        headerStyle={{ background: "#020617", color: "#ffffff", borderBottom: "1px solid rgba(255,255,255,0.08)" }}
        header={images.length > 0 ? `Gallery ${activeImageIndex + 1} of ${images.length}` : "Gallery"}
      >
        {images[activeImageIndex] ? (
          <div
            className="flex align-items-center justify-content-center"
            style={{ minHeight: "calc(100vh - 8rem)", gap: "1rem", padding: "1rem", background: "#020617" }}
          >
            <Button icon="pi pi-chevron-left" rounded outlined severity="contrast" autoFocus onClick={showPreviousImage} />
            <div className="flex align-items-center justify-content-center" style={{ flex: 1, height: "100%" }}>
              {images[activeImageIndex].indexOf(GALLERY_VIDEO) !== -1 ? (
                <video controls autoPlay style={{ maxWidth: "100%", maxHeight: "80vh", borderRadius: "18px" }}>
                  <source src={images[activeImageIndex]} type="video/mp4" />
                </video>
              ) : (
                <img
                  src={images[activeImageIndex]}
                  alt={`Gallery ${activeImageIndex + 1}`}
                  style={{ maxWidth: "100%", maxHeight: "80vh", objectFit: "contain", borderRadius: "18px" }}
                />
              )}
            </div>
            <Button icon="pi pi-chevron-right" rounded outlined severity="contrast" onClick={showNextImage} />
          </div>
        ) : null}
      </Dialog>

      <Dialog visible={dialogVisible} modal header="Contact us" style={{ width: mobile ? "90%" : "50rem" }} onHide={() => setDialogVisible(false)}>
        <div className="grid">
          <div className="col p-3">
            <GoogleMapEmbed lat={businessData?.latitude} lng={businessData?.longitude} />
            <i className="pi pi-map-marker" style={{ fontSize: "2em", color: "black" }}></i>
            <span>{businessData?.formattedAddress}</span>
          </div>
          <div className="col p-3">
            <div className="mb-3"><strong>WE'D LOVE TO HEAR FROM YOU!</strong></div>
            <form>
              <div className="field">
                <span className="p-float-label">
                  <Controller
                    name="fullName" 
                    control={control}
                    rules={{
                      required: "Name is required.",
                      maxLength: { value: 100, message: "Name must be at most 100 characters long." },
                    }}
                    render={({ field, fieldState }) => (
                      <InputText id={field.name} {...field} className={classNames({ "p-invalid": fieldState.invalid })} />
                    )}
                  />
                  <label htmlFor="fullName" className={classNames({ "p-error": errors.fullName })}>Full Name</label>
                </span>
                {getFormErrorMessage("fullName")}
              </div>

              <div className="field">
                <span className="p-float-label">
                  <Controller
                    name="email"
                    control={control}
                    rules={{
                      required: "Email is required.",
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Invalid email address." },
                    }}
                    render={({ field, fieldState }) => (
                      <InputText id={field.name} {...field} className={classNames({ "p-invalid": fieldState.invalid })} />
                    )}
                  />
                  <label htmlFor="email" className={classNames({ "p-error": errors.email })}>Email</label>
                </span>
                {getFormErrorMessage("email")}
              </div>

              <div className="field">
                <span className="p-float-label">
                  <Controller
                    name="phoneNumber"
                    control={control}
                    rules={{
                      required: "Phone number is required.",
                      pattern: { value: /^\+?[1-9]\d{1,14}$/, message: "Invalid phone number." },
                    }}
                    render={({ field, fieldState }) => (
                      <InputText id={field.name} {...field} className={classNames({ "p-invalid": fieldState.invalid })} />
                    )}
                  />
                  <label htmlFor="phoneNumber" className={classNames({ "p-error": errors.phoneNumber })}>Phone Number</label>
                </span>
                {getFormErrorMessage("phoneNumber")}
              </div>

              <div className="field">
                <span className="p-float-label">
                  <Controller
                    name="message"
                    control={control}
                    rules={{
                      required: "Message is required.",
                      maxLength: { value: 500, message: "Message must be at most 500 characters long." },
                    }}
                    render={({ field, fieldState }) => (
                      <InputTextarea rows={5} style={{ width: "100%" }} id={field.name} {...field} className={classNames({ "p-invalid": fieldState.invalid })} />
                    )}
                  />
                  <label htmlFor="message" className={classNames({ "p-error": errors.message })}>Message</label>
                </span>
                {getFormErrorMessage("message")}
              </div>

              <div>
                <h4>Inquiry Type:</h4>
                <div className="p-field-radiobutton">
                  {WEBSITE_INQUIRY_TYPES.map((option) => (
                    <div key={option.value} className="p-field-radiobutton">
                      <Controller
                        name="inquiryType"
                        control={control}
                        rules={{ required: "Please select an option." }}
                        render={({ field }) => (
                          <RadioButton
                            {...field}
                            inputId={option.value}
                            value={option.value}
                            checked={field.value === option.value}
                            onChange={(e) => field.onChange(e.value)}
                          />
                        )}
                      />
                      <label htmlFor={option.value}>{option.name}</label>
                    </div>
                  ))}
                </div>
                {getFormErrorMessage("inquiryType")}
              </div>

              <div>
                <ReCaptcha sitekey={process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY} onChange={(value) => setRecaptchaValue(value)} />
                <div className="mt-2">
                  {renderWarmWelcomeButton("Submit", handleSubmit(onSubmit), { type: "submit", outlined: true, loading: formSubmitting, disabled: !recaptchaValue, telemetryAction: "website_submit_inquiry_form" })}
                </div>
              </div>
            </form>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default PublishWeb;
